import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Tabs,
  Tab,
  Box,
  Typography,
  Chip,
  FormControlLabel,
  Checkbox,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  Alert,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import CloseIcon from '@mui/icons-material/Close';
import RepeatIcon from '@mui/icons-material/Repeat';
import dayjs, { Dayjs } from 'dayjs';
import { Task, TaskStatus, TaskPriority, ScheduleType, RecurringFrequency, RecurringConfig, Staff, Store } from '../types';
import { lookupAPI, tasksAPI } from '../services/api';

interface TaskModalProps {
  open: boolean;
  task?: Partial<Task>;
  selectedStore?: Store | null;
  onClose: () => void;
  onSave: (task: Omit<Task, 'id'> | Task) => void;
  onDelete?: (taskId: number) => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const WEEKDAYS = [
  { value: 0, label: 'Sun' },
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
];

const TaskModal: React.FC<TaskModalProps> = ({
  open,
  task,
  selectedStore,
  onClose,
  onSave,
  onDelete,
}) => {
  const [tabValue, setTabValue] = useState(0);
  const [staff, setStaff] = useState<Staff[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [notes, setNotes] = useState('');
  const [assigneeId, setAssigneeId] = useState<number | ''>('');
  const [priority, setPriority] = useState<TaskPriority>('Medium');
  const [status, setStatus] = useState<TaskStatus>('todo');
  const [storeId, setStoreId] = useState<number | ''>('');

  // Schedule state
  const [scheduleType, setScheduleType] = useState<ScheduleType>('none');
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs());
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [allDay, setAllDay] = useState(true);

  // Recurring state
  const [frequency, setFrequency] = useState<RecurringFrequency>('daily');
  const [weeklyDays, setWeeklyDays] = useState<number[]>([1, 3, 5]);
  const [endsMode, setEndsMode] = useState<'never' | 'onDate' | 'after'>('never');
  const [endsOnDate, setEndsOnDate] = useState<Dayjs | null>(dayjs().add(30, 'day'));
  const [endsAfterCount, setEndsAfterCount] = useState(30);

  // Apply to future (for recurring instances)
  const [applyToFuture, setApplyToFuture] = useState(false);

  // Load staff and stores on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        const [staffData, storesData] = await Promise.all([
          lookupAPI.getStaff(),
          lookupAPI.getStores(),
        ]);
        setStaff(staffData);
        setStores(storesData);
      } catch (err) {
        console.error('Error loading lookup data:', err);
      }
    };
    loadData();
  }, []);

  // Reset form when dialog opens/closes or task changes
  useEffect(() => {
    if (open) {
      setTabValue(0);
      setError(null);

      if (task) {
        setTitle(task.title || '');
        setDescription(task.description || '');
        setNotes(task.notes || '');
        setAssigneeId(task.assignee_id || '');
        setPriority(task.priority || 'Medium');
        setStatus(task.status || 'todo');
        setStoreId(task.store_id || selectedStore?.id || '');
        setScheduleType(task.schedule_type || 'none');
        setStartDate(task.start_date ? dayjs(task.start_date) : dayjs());
        setEndDate(task.end_date ? dayjs(task.end_date) : null);
        setAllDay(task.all_day !== false);

        if (task.recurring_config) {
          setFrequency(task.recurring_config.frequency || 'daily');
          setWeeklyDays(task.recurring_config.weeklyDays || [1, 3, 5]);
          setEndsMode(task.recurring_config.ends?.mode || 'never');
          if (task.recurring_config.ends?.endDate) {
            setEndsOnDate(dayjs(task.recurring_config.ends.endDate));
          }
          if (task.recurring_config.ends?.count) {
            setEndsAfterCount(task.recurring_config.ends.count);
          }
        } else {
          setFrequency('daily');
          setWeeklyDays([1, 3, 5]);
          setEndsMode('never');
          setEndsOnDate(dayjs().add(30, 'day'));
          setEndsAfterCount(30);
        }
      } else {
        // New task defaults
        setTitle('');
        setDescription('');
        setNotes('');
        setAssigneeId('');
        setPriority('Medium');
        setStatus('todo');
        setStoreId(selectedStore?.id || '');
        setScheduleType('none');
        setStartDate(dayjs());
        setEndDate(null);
        setAllDay(true);
        setFrequency('daily');
        setWeeklyDays([1, 3, 5]);
        setEndsMode('never');
        setEndsOnDate(dayjs().add(30, 'day'));
        setEndsAfterCount(30);
      }

      // Reset apply to future checkbox
      setApplyToFuture(false);
    }
  }, [open, task, selectedStore]);

  const handleWeeklyDayToggle = (day: number) => {
    setWeeklyDays(prev =>
      prev.includes(day)
        ? prev.filter(d => d !== day)
        : [...prev, day].sort()
    );
  };

  const handleSave = async () => {
    if (!title.trim()) {
      setError('Title is required');
      return;
    }
    if (!storeId) {
      setError('Store is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let recurringConfig: RecurringConfig | undefined;

      if (scheduleType === 'recurring') {
        recurringConfig = {
          frequency,
          weeklyDays: frequency === 'weekly' ? weeklyDays : undefined,
          ends: {
            mode: endsMode,
            endDate: endsMode === 'onDate' && endsOnDate ? endsOnDate.format('YYYY-MM-DD') : undefined,
            count: endsMode === 'after' ? endsAfterCount : undefined,
          },
        };
      }

      const taskData: Omit<Task, 'id'> | Task = {
        ...(task?.id ? { id: task.id } : {}),
        title: title.trim(),
        description: description.trim() || undefined,
        notes: notes.trim() || undefined,
        assignee_id: assigneeId || undefined,
        priority,
        status,
        store_id: storeId as number,
        schedule_type: scheduleType,
        start_date: scheduleType !== 'none' && startDate ? startDate.format('YYYY-MM-DD') : undefined,
        end_date: scheduleType === 'multi' && endDate ? endDate.format('YYYY-MM-DD') : undefined,
        all_day: allDay,
        recurring_config: recurringConfig,
      };

      // If this is a recurring instance and "Apply to all future" is checked,
      // also update the parent template
      if (isInstance && applyToFuture && task?.id) {
        await tasksAPI.updateTemplate(task.id, {
          title: title.trim(),
          description: description.trim() || undefined,
          notes: notes.trim() || undefined,
          assignee_id: assigneeId || undefined,
          priority,
        });
      }

      onSave(taskData as any);
      onClose();
    } catch (err) {
      setError('Failed to save task');
      console.error('Error saving task:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = () => {
    if (task?.id && onDelete) {
      if (window.confirm('Are you sure you want to delete this task?')) {
        onDelete(task.id);
        onClose();
      }
    }
  };

  const isInstance = !!task?.parent_task_id;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {task?.id ? 'Edit Task' : 'Create Task'}
          {isInstance && (
            <Chip
              icon={<RepeatIcon />}
              label="Recurring Instance"
              size="small"
              color="secondary"
              variant="outlined"
            />
          )}
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
          <Tab label="Details" />
          <Tab label="Schedule" disabled={isInstance} />
        </Tabs>
      </Box>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                placeholder="e.g., Check inventory levels"
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Assignee</InputLabel>
                <Select
                  value={assigneeId}
                  onChange={(e) => setAssigneeId(e.target.value as number)}
                  label="Assignee"
                >
                  <MenuItem value="">Unassigned</MenuItem>
                  {staff.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.staff_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth required>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={priority}
                  onChange={(e) => setPriority(e.target.value as TaskPriority)}
                  label="Priority"
                >
                  <MenuItem value="Low">Low</MenuItem>
                  <MenuItem value="Medium">Medium</MenuItem>
                  <MenuItem value="High">High</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth required>
                <InputLabel>Status</InputLabel>
                <Select
                  value={status}
                  onChange={(e) => setStatus(e.target.value as TaskStatus)}
                  label="Status"
                >
                  <MenuItem value="todo">To Do</MenuItem>
                  <MenuItem value="in_progress">In Progress</MenuItem>
                  <MenuItem value="done">Done</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Store</InputLabel>
                <Select
                  value={storeId}
                  onChange={(e) => setStoreId(e.target.value as number)}
                  label="Store"
                >
                  {stores.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.store_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                multiline
                rows={3}
                placeholder="Detailed description of the task..."
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                multiline
                rows={2}
                placeholder="Additional notes or comments..."
              />
            </Grid>

            {/* Apply to all future tasks checkbox - only shown for recurring instances */}
            {isInstance && task?.id && (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mt: 1 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={applyToFuture}
                        onChange={(e) => setApplyToFuture(e.target.checked)}
                      />
                    }
                    label={
                      <Typography variant="body2">
                        <strong>Apply changes to all future tasks</strong> - Updates the recurring template so future instances will use these values (title, description, notes, assignee, priority)
                      </Typography>
                    }
                  />
                </Alert>
              </Grid>
            )}
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Schedule Type</InputLabel>
                <Select
                  value={scheduleType}
                  onChange={(e) => setScheduleType(e.target.value as ScheduleType)}
                  label="Schedule Type"
                >
                  <MenuItem value="none">None</MenuItem>
                  <MenuItem value="single">One-off / Single Day</MenuItem>
                  <MenuItem value="multi">Multi-day Span</MenuItem>
                  <MenuItem value="recurring">Recurring</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {scheduleType !== 'none' && (
              <>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={allDay}
                        onChange={(e) => setAllDay(e.target.checked)}
                      />
                    }
                    label="All Day"
                  />
                </Grid>

                <Grid item xs={12} sm={scheduleType === 'multi' ? 6 : 12}>
                  <DatePicker
                    label={scheduleType === 'multi' ? 'Start Date' : 'Date'}
                    value={startDate}
                    onChange={(date) => setStartDate(date)}
                    slotProps={{ textField: { fullWidth: true } }}
                    format="DD/MM/YYYY"
                  />
                </Grid>

                {scheduleType === 'multi' && (
                  <Grid item xs={12} sm={6}>
                    <DatePicker
                      label="End Date"
                      value={endDate}
                      onChange={(date) => setEndDate(date)}
                      slotProps={{ textField: { fullWidth: true } }}
                      format="DD/MM/YYYY"
                      minDate={startDate || undefined}
                    />
                  </Grid>
                )}
              </>
            )}

            {scheduleType === 'recurring' && (
              <>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    Recurring Settings
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Frequency</InputLabel>
                    <Select
                      value={frequency}
                      onChange={(e) => setFrequency(e.target.value as RecurringFrequency)}
                      label="Frequency"
                    >
                      <MenuItem value="daily">Daily</MenuItem>
                      <MenuItem value="bidaily">Every 2 Days</MenuItem>
                      <MenuItem value="weekly">Weekly</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Ends</InputLabel>
                    <Select
                      value={endsMode}
                      onChange={(e) => setEndsMode(e.target.value as 'never' | 'onDate' | 'after')}
                      label="Ends"
                    >
                      <MenuItem value="never">Never</MenuItem>
                      <MenuItem value="onDate">On Date</MenuItem>
                      <MenuItem value="after">After N Occurrences</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {frequency === 'weekly' && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                      Select Days
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {WEEKDAYS.map((day) => (
                        <ToggleButton
                          key={day.value}
                          value={day.value}
                          selected={weeklyDays.includes(day.value)}
                          onChange={() => handleWeeklyDayToggle(day.value)}
                          sx={{
                            borderRadius: '50%',
                            width: 40,
                            height: 40,
                            fontSize: '0.75rem',
                          }}
                        >
                          {day.label}
                        </ToggleButton>
                      ))}
                    </Box>
                  </Grid>
                )}

                {endsMode === 'onDate' && (
                  <Grid item xs={12}>
                    <DatePicker
                      label="End Date"
                      value={endsOnDate}
                      onChange={(date) => setEndsOnDate(date)}
                      slotProps={{ textField: { fullWidth: true } }}
                      format="DD/MM/YYYY"
                      minDate={startDate || undefined}
                    />
                  </Grid>
                )}

                {endsMode === 'after' && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Number of Occurrences"
                      value={endsAfterCount}
                      onChange={(e) => setEndsAfterCount(Math.max(1, parseInt(e.target.value) || 1))}
                      inputProps={{ min: 1 }}
                    />
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Alert severity="info" sx={{ mt: 1 }}>
                    <Typography variant="body2">
                      <strong>Blocking Rule:</strong> The next recurring task will only be created when the current one is marked as Done.
                      If a task is overdue, it will be highlighted in red.
                    </Typography>
                  </Alert>
                </Grid>
              </>
            )}
          </Grid>
        </TabPanel>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        {task?.id && onDelete && (
          <Button
            onClick={handleDelete}
            color="error"
            sx={{ mr: 'auto' }}
          >
            Delete
          </Button>
        )}
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading}
        >
          {loading ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TaskModal;
